/**
 * Ball - Physics-driven football.
 * All drag/damping handled by Cannon.js native body properties (set in PhysicsSystem).
 * No manual velocity hacks — the physics engine does the work.
 */

import * as BABYLON from "babylonjs";
import { PhysicsSystem } from "../systems/PhysicsSystem";
import { FIELD_LENGTH } from "../core/SceneManager";
import { GOAL_HALF_WIDTH } from "./Goal";

export const BALL_RADIUS = 0.2; // Increased for better visibility

const BALL_MASS = 0.43;  // FIFA standard ~410-450g
const BALL_RESTITUTION = 0.6;  
const BALL_FRICTION = 0.4;  // Slightly increased for better engine-driven rolling
const MAX_SPEED = 35;
const BOUNCE_RESTITUTION = 0.6;  

// Stadium bounds - if ball goes beyond these, respawn it
const STADIUM_BOUND_X = 22;
const STADIUM_BOUND_Z = 35;

// Hard bounds — keep ball well inside the wall interior faces so it can't
// reach the pocket where thick walls overlap at corners
const BOUND_X = 19.7;
const BOUND_Z = (FIELD_LENGTH / 2) - 0.3; // 29.7 if field length is 60
const GOAL_Z_LIMIT = (FIELD_LENGTH / 2) + 1.2; // 31.2 if field length is 60

// Corner stuck detection — aggressive
const CORNER_X = 14;
const CORNER_Z = 25;
const STUCK_SPEED = 0.5;
const STUCK_TIME = 0.3;

export class Ball {
  public mesh: BABYLON.Mesh;
  private _stuckTimer = 0;

  constructor(scene: BABYLON.Scene, shadowGenerator?: BABYLON.ShadowGenerator) {
    this.mesh = BABYLON.MeshBuilder.CreateSphere(
      "ball",
      { diameter: BALL_RADIUS * 2, segments: 32 },
      scene
    );
    this.mesh.position = new BABYLON.Vector3(0, BALL_RADIUS + 0.02, 0);

    // ELITE football material (PBR)
    const mat = new BABYLON.PBRMaterial("ballMat", scene);
    
    // Create high-res procedural football pattern texture
    const ballTexture = new BABYLON.DynamicTexture("footballPattern", { width: 1024, height: 512 }, scene);
    const ctx = ballTexture.getContext();
    
    // White base with slight texture
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, 1024, 512);
    
    // Draw panel seams
    ctx.strokeStyle = "#DDDDDD";
    ctx.lineWidth = 3;
    
    // Draw black patches (pentagons)
    ctx.fillStyle = "#111111";
    const patchPositions = [
      [256, 128], [768, 128], [512, 256], [256, 384], [768, 384],
      [128, 256], [896, 256], [512, 64], [512, 448]
    ];
    
    patchPositions.forEach(([x, y]) => {
      ctx.beginPath();
      const sides = 5;
      const radius = 75;
      for (let i = 0; i < sides; i++) {
        const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
        const px = x + radius * Math.cos(angle);
        const py = y + radius * Math.sin(angle);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    });
    
    // Add subtle leather grain noise
    for(let i = 0; i < 8000; i++) {
        const x = Math.random() * 1024;
        const y = Math.random() * 512;
        const gray = Math.floor(Math.random() * 20);
        ctx.fillStyle = `rgba(${gray},${gray},${gray},0.03)`;
        ctx.fillRect(x, y, 2, 2);
    }
    
    ballTexture.update();
    
    mat.albedoTexture = ballTexture;
    mat.roughness = 0.15;
    mat.metallic = 0.0;
    
    // Add clear coat for that premium "new ball" sheen
    mat.clearCoat.isEnabled = true;
    mat.clearCoat.intensity = 0.6;
    mat.clearCoat.roughness = 0.1;
    
    this.mesh.material = mat;

    // Physics impostor — damping is configured inside PhysicsSystem.sphereImpostor
    PhysicsSystem.sphereImpostor(this.mesh, BALL_MASS, BALL_RESTITUTION, BALL_FRICTION);

    if (shadowGenerator) {
      shadowGenerator.addShadowCaster(this.mesh);
    }
  }

  public lastTouchedTime: number = Date.now();

  public registerTouch(): void {
    this.lastTouchedTime = Date.now();
  }

  private _tmpVec = new BABYLON.Vector3();

  update(dt: number): void {
    // Safety check: if mesh is disposed, we can't do anything
    if (this.mesh.isDisposed()) return;

    // Safety check: if parent is disposed, detach immediately
    if (this.mesh.parent && this.mesh.parent.isDisposed()) {
      console.warn("Ball parent was disposed! Detaching...");
      this.mesh.parent = null;
    }

    const imp = this.mesh.physicsImpostor;
    if (!imp) return;

    const vel = imp.getLinearVelocity();
    if (!vel) return;

    // Treat significant movement as "activity" so ball doesn't vanish while rolling
    const speed = vel.length();
    if (speed > 0.2) {
      this.lastTouchedTime = Date.now();
    }

    // Check for inactivity (5 seconds)
    // Don't respawn if ball is attached to a player/GK (has parent)
    if (!this.mesh.parent && Date.now() - this.lastTouchedTime > 5000) {
      console.log("Ball inactive for 5s, respawning...");
      this.resetPosition();
      return;
    }

    // Speed cap
    if (speed > MAX_SPEED) {
      this._tmpVec.copyFrom(vel).normalize().scaleInPlace(MAX_SPEED);
      imp.setLinearVelocity(this._tmpVec);
    }

    // Respawn if ball falls below the map
    const pos = this.mesh.position;
    if (pos.y < -5 || pos.y > 20) {
      this.resetPosition();
      return;
    }

    // Respawn if ball goes outside stadium bounds
    if (Math.abs(pos.x) > STADIUM_BOUND_X || Math.abs(pos.z) > STADIUM_BOUND_Z) {
      this.resetPosition();
      return;
    }

    // CRITICAL: Check for invalid coordinates (NaN or Infinity)
    if (!isFinite(pos.x) || !isFinite(pos.y) || !isFinite(pos.z)) {
      console.warn("Ball has invalid coordinates, resetting...");
      this.resetPosition();
      return;
    }

    // Ensure ball is always visible
    if (!this.mesh.isVisible) {
      this.mesh.isVisible = true;
    }

    // Hard bounds - bounce ball off walls
    if (Math.abs(pos.x) > BOUND_X) {
      pos.x = Math.sign(pos.x) * BOUND_X;
      this._tmpVec.copyFrom(vel);
      this._tmpVec.x = -vel.x * BOUNCE_RESTITUTION; // Reflect and dampen
      imp.setLinearVelocity(this._tmpVec);
    }

    // Determine current Z bound based on whether ball is in goal mouth
    const inGoalArea = Math.abs(pos.x) < GOAL_HALF_WIDTH;
    const currentBoundZ = inGoalArea ? GOAL_Z_LIMIT : BOUND_Z;

    if (Math.abs(pos.z) > currentBoundZ) {
      pos.z = Math.sign(pos.z) * currentBoundZ;
      this._tmpVec.copyFrom(vel);
      this._tmpVec.z = -vel.z * BOUNCE_RESTITUTION; // Reflect and dampen
      imp.setLinearVelocity(this._tmpVec);
    }

    // Corner stuck detection
    const nearCorner = Math.abs(pos.x) > CORNER_X && Math.abs(pos.z) > CORNER_Z;
    const slowEnough = speed < STUCK_SPEED;

    if (nearCorner && slowEnough) {
      this._stuckTimer += dt;
      if (this._stuckTimer > STUCK_TIME) {
        // Teleport ball away from corner and push toward center
        pos.x = Math.sign(pos.x) * (CORNER_X - 1);
        pos.z = Math.sign(pos.z) * (CORNER_Z - 1);
        this._tmpVec.set(-Math.sign(pos.x) * 3, 0.5, -Math.sign(pos.z) * 3);
        imp.setLinearVelocity(BABYLON.Vector3.Zero());
        imp.setAngularVelocity(BABYLON.Vector3.Zero());
        imp.applyImpulse(this._tmpVec, this.mesh.getAbsolutePosition());
        this._stuckTimer = 0;
      }
    } else {
      this._stuckTimer = 0;
    }
  }

  resetPosition(): void {
    console.log("Ball.resetPosition() called");
    
    // Clear any parent (detached from goalkeeper hands)
    if (this.mesh.parent) {
      console.log(`Ball had parent: ${this.mesh.parent.name}, detaching...`);
      this.mesh.parent = null;
    }
    
    // Reset position to center field
    this.mesh.position = new BABYLON.Vector3(0, BALL_RADIUS + 0.05, 0);
    this.mesh.rotation = BABYLON.Vector3.Zero();
    
    // Ensure ball is visible
    this.mesh.isVisible = true;
    
    const imp = this.mesh.physicsImpostor;
    if (imp) {
      // Force sleep and wake to reset internal physics state
      imp.sleep();
      
      // Reset velocity
      imp.setLinearVelocity(BABYLON.Vector3.Zero());
      imp.setAngularVelocity(BABYLON.Vector3.Zero());
      
      // Force position update to physics body
      imp.forceUpdate();
      
      // Wake up
      setTimeout(() => {
        if (imp && !imp.isDisposed) {
          imp.wakeUp();
          imp.setLinearVelocity(new BABYLON.Vector3(0, 0.1, 0)); // Tiny upward nudge
        }
      }, 0);
      
      console.log("Ball physics body reset and woken up");
    } else {
      console.warn("Ball has no physics impostor!");
    }
    
    // Reset stuck timer
    this._stuckTimer = 0;
    
    // Reset touch timer
    this.lastTouchedTime = Date.now();
    
    console.log(`Ball reset complete - position: ${this.mesh.position.toString()}, visible: ${this.mesh.isVisible}`);
  }

  /** 
   * Apply a physics-based kick.
   * @param direction Kick direction
   * @param power Impulse magnitude
   * @param playerVelocity Optional player velocity for momentum transfer
   * @param spin Optional spin vector (angular velocity add)
   */
  kick(direction: BABYLON.Vector3, power: number, playerVelocity?: BABYLON.Vector3, spin?: BABYLON.Vector3): void {
    this.registerTouch();
    const imp = this.mesh.physicsImpostor;
    if (!imp) return;

    const dir = direction.normalize();
    const impulse = dir.scale(power);
    
    // Transfer portion of player's momentum
    if (playerVelocity) {
      const momentumImpulse = playerVelocity.scale(0.2 * BALL_MASS);
      impulse.addInPlace(momentumImpulse);
    }

    imp.applyImpulse(impulse, this.mesh.getAbsolutePosition());

    if (spin) {
      const currentAngular = imp.getAngularVelocity() || BABYLON.Vector3.Zero();
      imp.setAngularVelocity(currentAngular.add(spin));
    }
  }
}
